/**
 * @license
 * Copyright Paysafe 2026. All rights reserved.
 */

const CURRENCY_SYMBOLS: { [key: string]: string } = {
  USD: '$',
  EUR: '€',
  GBP: '£'
};

const CURRENCY_SCALE: { [key: string]: number} = {
  USD: 2,
  EUR: 2,
  GBP: 2,
  JPY: 0
};

/**
 * Formats a currency symbol with a currency amount(from minor to major units).
 * @param amount - The amount in minor units as number (e.g., 1543 for $15.43)
 * @param currencyCode - The ISO currency code (e.g., 'USD', 'EUR', 'GBP')
 * @returns Formatted amount string, e.g. $15.43
 */
export function formatCurrencyAmount(amount: number, currencyCode: string): string {
  const scale = getCurrencyScale(currencyCode);
  const divisor = 10 ** scale;
  const sign = amount < 0 ? '-' : '';
  const absoluteAmount = amount < 0 ? -amount : amount;

  // Get major units (integer part)
  const majorUnits = Math.trunc(absoluteAmount / divisor);

  // Get minor units (decimal part)
  const remainder = absoluteAmount % divisor;

  if (scale === 0) {
    return getCurrencySymbol(currencyCode).concat(`${sign}${majorUnits}`);
  }

  // Pad minor units with leading zeros to match scale
  const minorUnitsStr = remainder.toString().padStart(scale, '0');
  const value = `${sign}${majorUnits}.${minorUnitsStr}`;

  return getCurrencySymbol(currencyCode).concat(value);
}

/**
 * Returns the currency symbol for a given currency code.
 * Falls back to currencyCode.
 *
 * @param currencyCode - The ISO currency code (e.g., 'USD', 'EUR', 'GBP')
 * @returns The currency symbol or currencyCode if not found
 */
function getCurrencySymbol(currencyCode: string): string {
  if (!currencyCode) {
    return '';
  }
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
}

/**
 * Returns the currency scale for a given currency code.
 * Falls back to the default scale ot 2.
 *
 * @param currencyCode - The ISO currency code (e.g., 'USD', 'EUR', 'GBP')
 * @returns The currency scale or by 2 if not found
 */
function getCurrencyScale(currencyCode: string): number {
  return CURRENCY_SCALE[currencyCode] ?? 2;
}
